'use client';

import { useMemo, useState } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { AnimatePresence, motion } from 'framer-motion';
import { Filters } from '@/components/table/filters';
import { TableComponent } from '@/components/table/table';
import { Pagination } from '@/components/table/pagination';
// Generic table wrapper used across different data shapes

import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Button } from '../ui/button';
import { ArrowUpRightIcon, Folder } from 'lucide-react';
import { useTranslations } from '@/hooks/useTranslations';
import { BulkDeleteUsers } from './columns/bulk-delete-users';

interface TableWrapperProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  filters: boolean;
  pagination: boolean;
  count?: number;
  isFull?: boolean;
  translation: (key: string) => string;
  commonTranslations?: (key: string) => string;
  searchLabel: string;
  cta?: {
    label: string;
    onClick: () => void;
  };
  extraFilters?: React.ReactNode;
  emptyState?: {
    title?: string;
    description?: string;
    icon?: React.ReactNode;
    hideLearnMore?: boolean;
  };
  bulkActions?:
    | React.ReactNode
    | ((table: ReturnType<typeof useReactTable<TData>>) => React.ReactNode);
  selectedCountLabel?: string;
}

export function TableWrapper<TData>({
  data,
  columns,
  filters,
  pagination,
  count,
  cta,
  translation,
  searchLabel,
  extraFilters,
  emptyState,
  bulkActions,
  selectedCountLabel,
}: TableWrapperProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const handleSortingChange = setSorting;
  const handleColumnFiltersChange = setColumnFilters;
  const handleColumnVisibilityChange = setColumnVisibility;
  const handleRowSelectionChange = setRowSelection;

  // Memoize table configuration
  const tableConfig = useMemo(
    () => ({
      data,
      columns,
      onSortingChange: handleSortingChange,
      onColumnFiltersChange: handleColumnFiltersChange,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      onColumnVisibilityChange: handleColumnVisibilityChange,
      onRowSelectionChange: handleRowSelectionChange,
      state: {
        sorting,
        columnFilters,
        columnVisibility,
        rowSelection,
      },
      initialState: {
        pagination: {
          pageSize: count || 10,
          pageIndex: 0,
        },
      },
    }),
    [
      data,
      columns,
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      count,
      handleSortingChange,
      handleColumnFiltersChange,
      handleColumnVisibilityChange,
      handleRowSelectionChange,
    ]
  );

  /* eslint-disable react-hooks/incompatible-library */
  const table = useReactTable(tableConfig);
  const tEmpty = useTranslations('Empty');

  const isEmpty = table.getRowModel().rows.length === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }}
    >
      {isEmpty ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              {emptyState?.icon || <Folder />}
            </EmptyMedia>
            <EmptyTitle>{emptyState?.title || tEmpty('title')}</EmptyTitle>
            <EmptyDescription>
              {emptyState?.description || tEmpty('description')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {cta && (
              <div className="flex gap-2">
                <Button onClick={cta.onClick}>{cta.label}</Button>
              </div>
            )}
          </EmptyContent>
          {!emptyState?.hideLearnMore && (
            <Button
              variant="link"
              asChild
              className="text-muted-foreground"
              size="sm"
            >
              <a href="#">
                {tEmpty('learnMore')} <ArrowUpRightIcon />
              </a>
            </Button>
          )}
        </Empty>
      ) : (
        <div>
          {filters && (
            <Filters<TData>
              table={table}
              translation={translation}
              searchLabel={searchLabel}
              cta={cta}
              extraFilters={extraFilters}
            />
          )}

          <TableComponent<TData> table={table} loading={false} />

          <AnimatePresence>
            {Object.keys(rowSelection).length > 0 && (
              <motion.div
                key="selection-toast"
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 24, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed bottom-8 left-4 right-4 z-50 mx-auto max-w-xl rounded-xl border bg-card px-4 py-3 shadow-2xl"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium">
                    {selectedCountLabel?.replace(
                      '{count}',
                      String(Object.keys(rowSelection).length)
                    ) ||
                      translation('usersSelected')?.replace(
                        '{count}',
                        String(Object.keys(rowSelection).length)
                      ) ||
                      `${Object.keys(rowSelection).length} selected`}
                  </span>
                  <div className="flex shrink-0 items-center gap-2">
                    {typeof bulkActions === 'function'
                      ? bulkActions(table)
                      : bulkActions || <BulkDeleteUsers table={table} />}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {pagination && <Pagination<TData> table={table} />}
        </div>
      )}
    </motion.div>
  );
}
